import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Snackbar,
    Alert
} from '@mui/material';
import InputMontoMui from '../InputMontoMui';
import { actualizarGastoMedico, obtenerGastoMedicoPorId } from '../../services/gastoMedicoService';
import { listarMedicos } from '../../services/medicoService';
import { getTramites } from '../../services/tramiteService';
import { getClientes } from '../../services/clienteService';
import { getTipoTramites } from '../../services/tipoTramiteService';

const ModalEditarGastoMedico = ({ open, onClose, gastoId, onGastoActualizado }) => {
    const [formData, setFormData] = useState({
        monto: '',
        fecha: '',
        idMedico: '',
        idTramite: '',
        comentario: ''
    });
    const [medicos, setMedicos] = useState([]);
    const [tramites, setTramites] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [tiposTramite, setTiposTramite] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cargandoDatos, setCargandoDatos] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [errors, setErrors] = useState({});

    // Cargar datos del gasto médico y datos necesarios
    const cargarDatos = async () => {
        if (!gastoId) return;

        try {
            setCargandoDatos(true);

            const [gastoData, medicosData, tramitesData, clientesData, tiposTramiteData] = await Promise.all([
                obtenerGastoMedicoPorId(gastoId),
                listarMedicos(),
                getTramites(),
                getClientes(),
                getTipoTramites()
            ]);

            setMedicos(medicosData);
            setTramites(tramitesData);
            setClientes(clientesData);
            setTiposTramite(tiposTramiteData);

            if (gastoData) {
                setFormData({
                    monto: gastoData.monto || '',
                    fecha: gastoData.fecha || new Date().toISOString().split('T')[0],
                    idMedico: gastoData.idMedico || '',
                    idTramite: gastoData.idTramite || '',
                    comentario: gastoData.comentario || ''
                });
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);
            setSnackbar({ open: true, message: 'Error al cargar los datos del gasto', severity: 'error' });
        } finally {
            setCargandoDatos(false);
        }
    };

    // Obtener nombre del cliente por CUIL
    const obtenerNombreCliente = (cuil) => {
        const cliente = clientes.find(c => c.cuil === cuil);
        return cliente ? `${cliente.nombre} ${cliente.apellido}` : cuil;
    };

    // Obtener nombre del tipo de trámite
    const obtenerTipoTramite = (idTipoTramite) => {
        const tipo = tiposTramite.find(t => t.idTipoTramite === idTipoTramite);
        return tipo ? tipo.nombre : 'N/A';
    };

    // Formatear opción de trámite para el dropdown
    const formatearOpcionTramite = (tramite) => {
        const nombreCliente = obtenerNombreCliente(tramite.idCliente);
        const tipoTramite = obtenerTipoTramite(tramite.idTipoTramite);
        return `${nombreCliente} (${tipoTramite})`;
    };

    // Validaciones (médico ahora es opcional)
    const validarFormulario = () => {
        const nuevosErrores = {};

        if (!formData.monto || parseFloat(formData.monto) <= 0) {
            nuevosErrores.monto = 'El monto debe ser mayor a 0';
        }

        if (!formData.fecha) {
            nuevosErrores.fecha = 'La fecha es requerida';
        }

        if (!formData.idTramite) {
            nuevosErrores.idTramite = 'Debe seleccionar un trámite';
        }

        setErrors(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Enviar formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validarFormulario()) {
            return;
        }

        setLoading(true);
        try {
            const gastoData = {
                monto: parseFloat(formData.monto),
                fecha: formData.fecha,
                idTramite: formData.idTramite,
                // Campos opcionales - solo se envían si tienen valor
                ...(formData.idMedico && { idMedico: formData.idMedico }),
                ...(formData.comentario && { comentario: formData.comentario })
            };

            await actualizarGastoMedico(gastoId, gastoData);
            setSnackbar({ open: true, message: 'Gasto actualizado correctamente', severity: 'success' });

            setTimeout(() => {
                onClose();
                if (onGastoActualizado) {
                    onGastoActualizado();
                }
            }, 1000);

        } catch (error) {
            console.error('Error al actualizar gasto:', error);
            setSnackbar({ open: true, message: 'Error al actualizar el gasto', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Limpiar formulario al cerrar
    const handleClose = () => {
        setFormData({
            monto: '',
            fecha: '',
            idMedico: '',
            idTramite: '',
            comentario: ''
        });
        setErrors({});
        onClose();
    };

    useEffect(() => {
        if (open && gastoId) {
            cargarDatos();
        }
    }, [open, gastoId]);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center'
                    }}
                >
                    Editar Gasto
                </DialogTitle>

                <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ mt: 2 }}>
                        {cargandoDatos ? (
                            <div className="text-center my-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                                <p className="mt-2">Cargando datos del gasto...</p>
                            </div>
                        ) : (
                            <>
                                {/* Monto */}
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputMontoMui
                                        label="Monto *"
                                        value={formData.monto}
                                        onChange={(v) => {
                                            setFormData(prev => ({ ...prev, monto: v }));
                                            if (errors.monto) setErrors(prev => ({ ...prev, monto: '' }));
                                        }}
                                        error={!!errors.monto}
                                        helperText={errors.monto}
                                        required
                                    />
                                </FormControl>

                                {/* Fecha */}
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <TextField
                                        label="Fecha del gasto *"
                                        name="fecha"
                                        type="date"
                                        value={formData.fecha}
                                        onChange={handleChange}
                                        error={!!errors.fecha}
                                        helperText={errors.fecha}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                    />
                                </FormControl>

                                {/* Trámite */}
                                <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.idTramite}>
                                    <InputLabel>Trámite *</InputLabel>
                                    <Select
                                        name="idTramite"
                                        value={formData.idTramite}
                                        onChange={handleChange}
                                        label="Trámite *"
                                        required
                                    >
                                        {tramites.map((tramite) => (
                                            <MenuItem key={tramite.idTramite} value={tramite.idTramite}>
                                                {formatearOpcionTramite(tramite)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.idTramite && (
                                        <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px' }}>
                                            {errors.idTramite}
                                        </div>
                                    )}
                                </FormControl>

                                {/* Médico - OPCIONAL */}
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel>Médico</InputLabel>
                                    <Select
                                        name="idMedico"
                                        value={formData.idMedico}
                                        onChange={handleChange}
                                        label="Médico"
                                    >
                                        <MenuItem value="">
                                            <em>Sin médico asignado</em>
                                        </MenuItem>
                                        {medicos.map((medico) => (
                                            <MenuItem key={medico.idMedico} value={medico.idMedico}>
                                                {medico.nombre} {medico.apellido}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Comentario - OPCIONAL */}
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <TextField
                                        label="Comentario"
                                        name="comentario"
                                        value={formData.comentario}
                                        onChange={handleChange}
                                        multiline
                                        rows={3}
                                        placeholder="Descripción del gasto (opcional)..."
                                    />
                                </FormControl>

                                {/* Leyenda de campos requeridos */}
                                <div style={{ fontSize: '0.75rem', color: '#666', textAlign: 'right' }}>
                                    * Campos requeridos
                                </div>
                            </>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ p: 3, gap: 1 }}>
                        <button
                            type="button"
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={handleClose}
                            disabled={loading || cargandoDatos}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary rounded-pill px-4 d-flex align-items-center gap-2"
                            disabled={loading || cargandoDatos}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    Actualizando...
                                </>
                            ) : (
                                'Actualizar Gasto'
                            )}
                        </button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModalEditarGastoMedico;